import { useLocation } from "@tanstack/react-router";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/** Stored preference. `system` follows `prefers-color-scheme`. */
export type ThemePreference = "light" | "dark" | "system";
/** What is actually painted. */
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "bday_theme";

/**
 * Dark mode is admin-only for now — the customer site is styled for a light
 * surface. Must match the pre-paint gate in `client/index.html`.
 */
export function isDarkModeRoute(pathname: string): boolean {
  return pathname.startsWith("/admin");
}

interface ThemeContextValue {
  /** The stored preference (what the toggle shows). */
  preference: ThemePreference;
  /**
   * What the document is painted in right now. `light` outside dark-mode
   * routes regardless of preference — so consumers that pick colors by theme
   * (avatars, code blocks) always match the surface.
   */
  theme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  preference: "system",
  theme: "light",
  setPreference: () => {},
});

function readPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    // Storage blocked (private mode, etc.) — fall through to the default.
  }
  return "system";
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Holds the theme preference (persisted per browser in localStorage), resolves
 * it against the OS setting, and paints `<html>` — but only on dark-mode routes
 * (`isDarkModeRoute`). Gating on the ROUTE rather than on a mounted admin
 * component matters: the auth-loading splash renders before the admin shell
 * exists, and the `index.html` pre-paint snippet has already painted it dark.
 */
export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ResolvedTheme>(systemTheme);
  const isDarkRoute = useLocation({ select: (l) => isDarkModeRoute(l.pathname) });

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setSystem(mq.matches ? "dark" : "light");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Best effort — the in-memory state still applies for this session.
    }
  }, []);

  const resolved = preference === "system" ? system : preference;
  const theme: ResolvedTheme = isDarkRoute ? resolved : "light";

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    root.classList.toggle("light", theme !== "dark");
    root.style.colorScheme = theme;
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({ preference, theme, setPreference }),
    [preference, theme, setPreference],
  );

  return <ThemeContext value={value}>{children}</ThemeContext>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
